/**
 * PWA utility functions for checking installation status and capabilities
 */

export const isPWAInstalled = (): boolean => {
  // Check if app is running in standalone mode
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  
  // Check iOS standalone mode
  const isIOSStandalone = (window.navigator as any).standalone === true;
  
  // Check if running from installed PWA
  return isStandalone || isIOSStandalone;
};

export const isPWAInstallable = (): boolean => {
  // Check if browser supports PWA installation
  return 'serviceWorker' in navigator && 'BeforeInstallPromptEvent' in window;
};

export const getPWADisplayMode = (): string => {
  if (isPWAInstalled()) {
    return 'standalone';
  }
  
  if (window.matchMedia('(display-mode: minimal-ui)').matches) {
    return 'minimal-ui';
  }
  
  if (window.matchMedia('(display-mode: fullscreen)').matches) {
    return 'fullscreen';
  }
  
  return 'browser';
};

export const isOnMobile = (): boolean => {
  return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

export const getInstallInstructions = (): { title: string; steps: string[] } => {
  const userAgent = navigator.userAgent.toLowerCase();
  
  if (userAgent.includes('chrome') && !userAgent.includes('edg')) {
    return {
      title: 'Install on Chrome',
      steps: [
        'Tap the menu (⋮) in the top right corner',
        'Select "Add to Home screen" or "Install app"',
        'Tap "Add" or "Install" to confirm',
      ],
    };
  }
  
  if (userAgent.includes('safari') && userAgent.includes('iphone')) {
    return {
      title: 'Install on iPhone Safari',
      steps: [
        'Tap the Share button (📤) at the bottom of the screen',
        'Scroll down and tap "Add to Home Screen"',
        'Tap "Add" in the top right corner',
      ],
    };
  }
  
  if (userAgent.includes('safari') && userAgent.includes('ipad')) {
    return {
      title: 'Install on iPad Safari',
      steps: [
        'Tap the Share button (📤) in the address bar',
        'Tap "Add to Home Screen"',
        'Tap "Add" in the top right corner',
      ],
    };
  }
  
  if (userAgent.includes('firefox')) {
    return {
      title: 'Install on Firefox',
      steps: [
        'Tap the menu (⋮) in the top right corner',
        'Select "Install" or "Add to Home screen"',
        'Tap "Add" to confirm',
      ],
    };
  }
  
  return {
    title: 'Install App',
    steps: [
      'Look for an "Install" button or option in your browser menu',
      'Follow your browser\'s prompts to add the app to your home screen',
    ],
  };
};

// Handle orientation changes for PWA
export const setupOrientationHandler = (): void => {
  // Force orientation unlock on PWA
  if (isPWAInstalled()) {

    // Listen for orientation changes
    window.addEventListener('orientationchange', () => {
      // Force a layout recalculation after orientation change
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 100);
    });
  }
};

// Log PWA status for debugging
export const logPWAStatus = (): void => {
  console.log('PWA Status:', {
    isInstalled: isPWAInstalled(),
    isInstallable: isPWAInstallable(),
    displayMode: getPWADisplayMode(),
    isOnMobile: isOnMobile(),
    userAgent: navigator.userAgent,
    orientation: window.screen?.orientation?.type || 'unknown',
  });
};
